import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Radio,
  Select,
  Input,
  DatePicker,
  Button,
  Table,
  Row,
  Col,
  message,
  Tag,
  Alert,
  Icon,
  Tooltip,
  Collapse,
} from 'antd';
import moment from 'moment';
import {
  fetchDailyWeatherForLocation,
  exportWeatherToCSV,
  getSoilStatus,
  getForagingRisk,
  getOperationalAlert,
} from '../../../../services/weatherService';
import { getAllApiariesService } from '../../../../services/apiaryService';
import './WeatherReport.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Panel } = Collapse;

// Standard Quick Presets
const PRESET_LOCATIONS = [
  {
    id: 'Mirgaon',
    name: 'Mirgaon (Kanpur Dehat, UP)',
    rawName: 'Mirgaon_Kanpur_Dehat_UP',
    lat: 26.332268,
    lon: 79.622287,
  },
  {
    id: 'Gonda',
    name: 'Gonda (Garhwa, JH)',
    rawName: 'Gonda_Garhwa_JH',
    lat: 24.192109,
    lon: 83.69818,
  },
  {
    id: 'Muzaffarpur',
    name: 'Muzaffarpur (Bihar)',
    rawName: 'Muzaffarpur_Bihar',
    lat: 26.1209,
    lon: 85.3647,
  },
  {
    id: 'Bangalore',
    name: 'Bangalore (HQ)',
    rawName: 'Bangalore_HQ',
    lat: 12.9716,
    lon: 77.5946,
  },
];

/**
 * Robust helper to extract coordinates from all known Humblebee MongoDB apiary schema formats:
 * 1. geo_location.lat / geo_location.lng (or .lon)
 * 2. geo_location.coordinates: [lng, lat] (GeoJSON)
 * 3. location.lat / location.lng / location.latitude / location.longitude
 * 4. location.coordinates: [lng, lat] (GeoJSON)
 * 5. latitude / longitude / lat / lng / lon at top-level
 */
export const extractApiaryCoords = (a) => {
  if (!a) return null;
  let lat = null;
  let lon = null;

  // 1. Check geo_location
  if (a.geo_location) {
    if (a.geo_location.lat !== undefined && a.geo_location.lat !== null && a.geo_location.lat !== '') {
      lat = parseFloat(a.geo_location.lat);
    }
    if (a.geo_location.lng !== undefined && a.geo_location.lng !== null && a.geo_location.lng !== '') {
      lon = parseFloat(a.geo_location.lng);
    } else if (a.geo_location.lon !== undefined && a.geo_location.lon !== null && a.geo_location.lon !== '') {
      lon = parseFloat(a.geo_location.lon);
    }
    // GeoJSON [longitude, latitude]
    if ((lat === null || isNaN(lat) || lon === null || isNaN(lon)) && Array.isArray(a.geo_location.coordinates) && a.geo_location.coordinates.length >= 2) {
      lon = parseFloat(a.geo_location.coordinates[0]);
      lat = parseFloat(a.geo_location.coordinates[1]);
    }
  }

  // 2. Check location & address.coords
  if ((lat === null || isNaN(lat) || lon === null || isNaN(lon)) && a.address && a.address.coords) {
    if (a.address.coords.lat !== undefined && a.address.coords.lat !== null && a.address.coords.lat !== '') {
      lat = parseFloat(a.address.coords.lat);
    }
    if (a.address.coords.lng !== undefined && a.address.coords.lng !== null && a.address.coords.lng !== '') {
      lon = parseFloat(a.address.coords.lng);
    } else if (a.address.coords.lon !== undefined && a.address.coords.lon !== null && a.address.coords.lon !== '') {
      lon = parseFloat(a.address.coords.lon);
    }
  }

  if ((lat === null || isNaN(lat) || lon === null || isNaN(lon)) && a.location) {
    if (a.location.lat !== undefined && a.location.lat !== null && a.location.lat !== '') {
      lat = parseFloat(a.location.lat);
    }
    if (a.location.lng !== undefined && a.location.lng !== null && a.location.lng !== '') {
      lon = parseFloat(a.location.lng);
    } else if (a.location.lon !== undefined && a.location.lon !== null && a.location.lon !== '') {
      lon = parseFloat(a.location.lon);
    } else if (a.location.latitude !== undefined && a.location.latitude !== null && a.location.latitude !== '') {
      lat = parseFloat(a.location.latitude);
    } else if (a.location.longitude !== undefined && a.location.longitude !== null && a.location.longitude !== '') {
      lon = parseFloat(a.location.longitude);
    }
    // GeoJSON
    if ((lat === null || isNaN(lat) || lon === null || isNaN(lon)) && Array.isArray(a.location.coordinates) && a.location.coordinates.length >= 2) {
      lon = parseFloat(a.location.coordinates[0]);
      lat = parseFloat(a.location.coordinates[1]);
    }
  }

  // 3. Top-level coordinates
  if ((lat === null || isNaN(lat)) && a.latitude !== undefined && a.latitude !== null && a.latitude !== '') lat = parseFloat(a.latitude);
  if ((lat === null || isNaN(lat)) && a.lat !== undefined && a.lat !== null && a.lat !== '') lat = parseFloat(a.lat);
  if ((lon === null || isNaN(lon)) && a.longitude !== undefined && a.longitude !== null && a.longitude !== '') lon = parseFloat(a.longitude);
  if ((lon === null || isNaN(lon)) && a.lon !== undefined && a.lon !== null && a.lon !== '') lon = parseFloat(a.lon);
  if ((lon === null || isNaN(lon)) && a.lng !== undefined && a.lng !== null && a.lng !== '') lon = parseFloat(a.lng);

  if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon)) {
    return { lat, lon };
  }
  return null;
};

const WeatherReport = () => {
  // Mode: 'apiary' | 'gmaps' | 'latlon'
  const [locationMode, setLocationMode] = useState('apiary');

  // Input states
  const [selectedApiaryId, setSelectedApiaryId] = useState('Mirgaon');
  const [gmapsUrl, setGmapsUrl] = useState('');
  const [gmapsError, setGmapsError] = useState(false);
  const [parsedGmapsCoords, setParsedGmapsCoords] = useState(null);

  const [inputLat, setInputLat] = useState('');
  const [inputLon, setInputLon] = useState('');
  const [latlonError, setLatlonError] = useState(false);

  // Date Range (Default: Current Date to Current Date + 90 Days)
  const [dateRange, setDateRange] = useState([
    moment(),
    moment().add(90, 'days'),
  ]);
  const [showHelptext, setShowHelptext] = useState(true);

  // Data & Table state
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(1000);

  // Apiaries from backend
  const [apiariesList, setApiariesList] = useState([]);
  const [loadingApiaries, setLoadingApiaries] = useState(false);

  // Load system apiaries
  useEffect(() => {
    const loadApiaries = async () => {
      setLoadingApiaries(true);
      try {
        const data = await getAllApiariesService();
        console.log('🐝 WeatherReport loaded all apiaries from backend:', data);
        if (Array.isArray(data)) {
          const formatted = data.map((a, idx) => {
            const coords = extractApiaryCoords(a);
            const name = a.apiary_name || a.name || a.title || `Apiary #${idx + 1}`;
            const id = a.apiary_id || a._id || a.id || `apiary-${idx + 1}`;
            const locationParts = [a.village, a.block, a.district, a.state].filter(Boolean);
            const locationStr = locationParts.length > 0 ? locationParts.join(', ') : '';

            return {
              id: String(id),
              name: locationStr ? `${name} (${locationStr})` : name,
              rawName: name,
              lat: coords ? coords.lat : null,
              lon: coords ? coords.lon : null,
              hasCoords: !!coords,
              state: a.state || '',
              district: a.district || '',
              village: a.village || '',
            };
          });

          // Sort apiaries: first those with valid coordinates, alphabetically
          formatted.sort((a, b) => {
            if (a.hasCoords === b.hasCoords) {
              return a.rawName.localeCompare(b.rawName);
            }
            return a.hasCoords ? -1 : 1;
          });

          setApiariesList(formatted);
          console.log(`✅ Formatted ${formatted.length} apiaries (${formatted.filter(a => a.hasCoords).length} with GPS coordinates)`);
        }
      } catch (err) {
        console.warn('Could not load apiaries list:', err);
      } finally {
        setLoadingApiaries(false);
      }
    };
    loadApiaries();
  }, []);

  // Combined locations list
  const combinedLocations = useMemo(() => {
    return [...PRESET_LOCATIONS, ...apiariesList];
  }, [apiariesList]);

  // Auto-fetch default weather data on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      handleFetchWeather();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle Google Maps URL parsing
  const handleGmapsInput = (url) => {
    setGmapsUrl(url);
    if (!url || !url.trim()) {
      setGmapsError(false);
      setParsedGmapsCoords(null);
      return;
    }

    const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)|q=(-?\d+\.\d+),(-?\d+\.\d+)|destination=(-?\d+\.\d+),(-?\d+\.\d+)/;
    const match = url.match(regex);

    if (match) {
      setGmapsError(false);
      const lat = match[1] || match[3] || match[5];
      const lon = match[2] || match[4] || match[6];
      setParsedGmapsCoords({ lat: parseFloat(lat), lon: parseFloat(lon) });
    } else {
      setGmapsError(true);
      setParsedGmapsCoords(null);
    }
  };

  // 4-Decimal validation helper
  const hasMinFourDecimals = (val) => {
    if (!val) return false;
    const parts = val.toString().split('.');
    return parts.length === 2 && parts[1].length >= 4;
  };

  // Fetch Weather
  const handleFetchWeather = async () => {
    let targetLat = null;
    let targetLon = null;
    let targetName = 'Target Location';

    if (locationMode === 'apiary') {
      const selected = combinedLocations.find(
        (l) => String(l.id) === String(selectedApiaryId) || String(l.name) === String(selectedApiaryId)
      );
      if (!selected) {
        message.error('Please select an existing apiary / location from the dropdown.');
        return;
      }
      if (!selected.lat || !selected.lon || isNaN(selected.lat) || isNaN(selected.lon)) {
        message.warning(`Apiary "${selected.rawName || selected.name}" does not have GPS coordinates in the database. Please use Google Maps Link or Direct Lat/Long mode.`);
        return;
      }
      targetLat = Number(selected.lat);
      targetLon = Number(selected.lon);
      targetName = selected.rawName || selected.name;
    } else if (locationMode === 'gmaps') {
      let coords = parsedGmapsCoords;
      if (!coords && gmapsUrl) {
        const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)|q=(-?\d+\.\d+),(-?\d+\.\d+)|destination=(-?\d+\.\d+),(-?\d+\.\d+)/;
        const match = gmapsUrl.match(regex);
        if (match) {
          const lat = match[1] || match[3] || match[5];
          const lon = match[2] || match[4] || match[6];
          coords = { lat: parseFloat(lat), lon: parseFloat(lon) };
        }
      }
      if (!coords || isNaN(coords.lat) || isNaN(coords.lon)) {
        setGmapsError(true);
        message.error('Please paste a valid Google Maps link containing coordinate pin.');
        return;
      }
      targetLat = coords.lat;
      targetLon = coords.lon;
      targetName = 'Google_Maps_Pin';
    } else if (locationMode === 'latlon') {
      if (!inputLat || !inputLon || !hasMinFourDecimals(inputLat) || !hasMinFourDecimals(inputLon)) {
        setLatlonError(true);
        message.error('Coordinates require at least 4 decimal places (e.g. 26.3322, 79.6222).');
        return;
      }
      setLatlonError(false);
      targetLat = parseFloat(inputLat);
      targetLon = parseFloat(inputLon);
      targetName = `Coords_${targetLat}_${targetLon}`;
    }

    if (!dateRange || dateRange.length < 2) {
      message.error('Please select valid start and end dates.');
      return;
    }

    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const rows = await fetchDailyWeatherForLocation({
        locationName: targetName,
        lat: targetLat,
        lon: targetLon,
        startDate,
        endDate,
      });

      if (rows && rows.length > 0) {
        setTableData(rows);
        message.success(`Successfully fetched ${rows.length} daily weather records!`);
      } else {
        message.warning('No weather records found for the selected parameters.');
      }
    } catch (err) {
      console.error(err);
      message.error(err.message || 'Failed to fetch weather data.');
    } finally {
      setLoading(false);
    }
  };

  // Download Table Data as CSV
  const handleExportTableCSV = () => {
    if (!tableData || tableData.length === 0) {
      message.warning('No data available to export.');
      return;
    }
    const filename = `Daily_Weather_Report_${moment().format('YYYY-MM-DD_HHmm')}.csv`;
    exportWeatherToCSV(tableData, filename);
    message.success(`Exported ${tableData.length} records to ${filename}`);
  };

  // Filtered Table Data
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const q = searchQuery.toLowerCase();
    return tableData.filter(
      (r) =>
        (r.Location && r.Location.toLowerCase().includes(q)) ||
        (r.Date && r.Date.toLowerCase().includes(q)) ||
        (r.Soil_Status && r.Soil_Status.toLowerCase().includes(q)) ||
        (r.Foraging_Risk && r.Foraging_Risk.toLowerCase().includes(q)) ||
        (r.Operational_Alerts && r.Operational_Alerts.toLowerCase().includes(q)) ||
        String(r.Latitude).includes(q) ||
        String(r.Longitude).includes(q)
    );
  }, [tableData, searchQuery]);

  // Compute KPI Statistics
  const statistics = useMemo(() => {
    if (!tableData || tableData.length === 0) return null;

    let totalRain = 0;
    let sumTemp = 0;
    let tempCount = 0;
    let maxTemp = -Infinity;
    let minTemp = Infinity;
    let maxWind = 0;
    let sumSoil = 0;
    let soilCount = 0;
    let optimalCount = 0;

    tableData.forEach((r) => {
      if (r.Rainfall_mm !== null && !isNaN(r.Rainfall_mm)) totalRain += r.Rainfall_mm;
      if (r.Avg_Temp_C !== null && !isNaN(r.Avg_Temp_C)) {
        sumTemp += r.Avg_Temp_C;
        tempCount++;
      }
      if (r.Max_Temp_C !== null && !isNaN(r.Max_Temp_C)) {
        if (r.Max_Temp_C > maxTemp) maxTemp = r.Max_Temp_C;
      }
      if (r.Min_Temp_C !== null && !isNaN(r.Min_Temp_C)) {
        if (r.Min_Temp_C < minTemp) minTemp = r.Min_Temp_C;
      }
      if (r.Max_Wind_Speed_kmh !== null && !isNaN(r.Max_Wind_Speed_kmh)) {
        if (r.Max_Wind_Speed_kmh > maxWind) maxWind = r.Max_Wind_Speed_kmh;
      }
      if (r.Soil_Moisture_m3m3 !== null && !isNaN(r.Soil_Moisture_m3m3)) {
        sumSoil += r.Soil_Moisture_m3m3;
        soilCount++;
      }
      const risk = r.Foraging_Risk || getForagingRisk(r.Rainfall_mm, r.Max_Wind_Speed_kmh, r.Max_Temp_C, r.Min_Temp_C);
      if (risk === 'OPTIMAL') {
        optimalCount++;
      }
    });

    const foragPct = tableData.length ? Math.round((optimalCount / tableData.length) * 100) : 0;

    return {
      totalRecords: tableData.length,
      totalRain: totalRain.toFixed(1),
      avgTemp: tempCount ? (sumTemp / tempCount).toFixed(1) : 'N/A',
      maxTemp: maxTemp !== -Infinity ? maxTemp.toFixed(1) : 'N/A',
      minTemp: minTemp !== Infinity ? minTemp.toFixed(1) : 'N/A',
      maxWind: maxWind.toFixed(1),
      avgSoil: soilCount ? (sumSoil / soilCount).toFixed(3) : 'N/A',
      foragingOptimalPct: foragPct,
      optimalDaysCount: optimalCount,
    };
  }, [tableData]);

  // Ant Design Table Columns with exact color badges
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Date',
      dataIndex: 'Date',
      key: 'Date',
      width: 120,
      sorter: (a, b) => new Date(a.Date) - new Date(b.Date),
      render: (text) => (
        <strong style={{ color: '#2d3748' }}>
          {text ? (moment(text).isValid() ? moment(text).format('DD-MM-YYYY') : text) : ''}
        </strong>
      ),
    },
    {
      title: 'Rainfall (mm)',
      dataIndex: 'Rainfall_mm',
      key: 'Rainfall_mm',
      width: 120,
      sorter: (a, b) => (a.Rainfall_mm || 0) - (b.Rainfall_mm || 0),
      render: (val) => {
        if (val === null || val === undefined) return '—';
        return val > 0 ? (
          <span className="weather-metric-badge weather-rain-badge">
            🌧️ {val.toFixed(1)} mm
          </span>
        ) : (
          <span style={{ color: '#a0aec0' }}>0.0 mm</span>
        );
      },
    },
    {
      title: 'Avg Temp (°C)',
      dataIndex: 'Avg_Temp_C',
      key: 'Avg_Temp_C',
      width: 120,
      sorter: (a, b) => (a.Avg_Temp_C || 0) - (b.Avg_Temp_C || 0),
      render: (val) =>
        val !== null && val !== undefined ? (
          <span className="weather-metric-badge weather-temp-badge">
            {val.toFixed(1)} °C
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Max Temp (°C)',
      dataIndex: 'Max_Temp_C',
      key: 'Max_Temp_C',
      width: 120,
      sorter: (a, b) => (a.Max_Temp_C || 0) - (b.Max_Temp_C || 0),
      render: (val) => (val !== null && val !== undefined ? `${val.toFixed(1)} °C` : '—'),
    },
    {
      title: 'Min Temp (°C)',
      dataIndex: 'Min_Temp_C',
      key: 'Min_Temp_C',
      width: 120,
      sorter: (a, b) => (a.Min_Temp_C || 0) - (b.Min_Temp_C || 0),
      render: (val) => (val !== null && val !== undefined ? `${val.toFixed(1)} °C` : '—'),
    },
    {
      title: 'Max Wind (km/h)',
      dataIndex: 'Max_Wind_Speed_kmh',
      key: 'Max_Wind_Speed_kmh',
      width: 130,
      sorter: (a, b) => (a.Max_Wind_Speed_kmh || 0) - (b.Max_Wind_Speed_kmh || 0),
      render: (val) =>
        val !== null && val !== undefined ? (
          <span className="weather-metric-badge weather-wind-badge">
            💨 {val.toFixed(1)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Soil Moisture (m³/m³)',
      dataIndex: 'Soil_Moisture_m3m3',
      key: 'Soil_Moisture_m3m3',
      width: 140,
      sorter: (a, b) => (a.Soil_Moisture_m3m3 || 0) - (b.Soil_Moisture_m3m3 || 0),
      render: (val) =>
        val !== null && val !== undefined ? (
          <span className="weather-metric-badge weather-soil-badge">
            🌱 {val.toFixed(3)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: (
        <span>
          Soil Status{' '}
          <Tooltip title="In standard loam and clay-loam soils (typical of North/Central India), field capacity (maximum water held without pooling) usually sits around 0.35-0.38 m³/m³. Above 0.38, soil approaches total pore saturation, meaning heavy field trucks, hive transport vehicles, or field staff will cause soil compaction or get stuck in mud.">
            <Icon type="info-circle" style={{ color: '#718096', marginLeft: 4, cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'Soil_Status',
      key: 'Soil_Status',
      width: 145,
      sorter: (a, b) => {
        const sa = a.Soil_Status || getSoilStatus(a.Soil_Moisture_m3m3);
        const sb = b.Soil_Status || getSoilStatus(b.Soil_Moisture_m3m3);
        return sa.localeCompare(sb);
      },
      render: (val, record) => {
        const status = val || getSoilStatus(record.Soil_Moisture_m3m3);
        const colorMap = {
          SATURATED: '#1E88E5',
          IDEAL: '#4CAF50',
          DRY: '#FFB300',
        };
        const bg = colorMap[status] || '#718096';
        return (
          <span
            style={{
              backgroundColor: bg,
              color: '#ffffff',
              padding: '4px 10px',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.4px',
              display: 'inline-block',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: (
        <span>
          Foraging Risk{' '}
          <Tooltip title="Wind (> 20 km/h): Drops flight efficiency & forces low-altitude flying. Rain (> 10 mm): Raindrops knock bees out of air, forcing clustering in hive. Temp (< 12°C or > 38°C): Below 12°C wing muscles freeze; above 38°C bees shift to hive cooling.">
            <Icon type="info-circle" style={{ color: '#718096', marginLeft: 4, cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'Foraging_Risk',
      key: 'Foraging_Risk',
      width: 155,
      sorter: (a, b) => {
        const ra = a.Foraging_Risk || getForagingRisk(a.Rainfall_mm, a.Max_Wind_Speed_kmh, a.Max_Temp_C, a.Min_Temp_C);
        const rb = b.Foraging_Risk || getForagingRisk(b.Rainfall_mm, b.Max_Wind_Speed_kmh, b.Max_Temp_C, b.Min_Temp_C);
        return ra.localeCompare(rb);
      },
      render: (val, record) => {
        const risk = val || getForagingRisk(record.Rainfall_mm, record.Max_Wind_Speed_kmh, record.Max_Temp_C, record.Min_Temp_C);
        const colorMap = {
          'NO-FLIGHT': '#D32F2F',
          MARGINAL: '#FFA000',
          OPTIMAL: '#388E3C',
        };
        const bg = colorMap[risk] || '#718096';
        return (
          <span
            style={{
              backgroundColor: bg,
              color: '#ffffff',
              padding: '4px 10px',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.4px',
              display: 'inline-block',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            {risk}
          </span>
        );
      },
    },
    {
      title: (
        <span>
          Operational Alerts{' '}
          <Tooltip title="Wind (> 25 km/h): Sustained winds make hive inspections hazardous (risk of blowing off lids & bee stings). Heavy Rain/Mud (> 15 mm + Saturated Soil): Turns dirt roads into unnavigable mud tracks, impacting night migrations.">
            <Icon type="info-circle" style={{ color: '#718096', marginLeft: 4, cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'Operational_Alerts',
      key: 'Operational_Alerts',
      width: 175,
      sorter: (a, b) => {
        const oa = a.Operational_Alerts || getOperationalAlert(a.Rainfall_mm, a.Max_Wind_Speed_kmh, a.Soil_Moisture_m3m3);
        const ob = b.Operational_Alerts || getOperationalAlert(b.Rainfall_mm, b.Max_Wind_Speed_kmh, b.Soil_Moisture_m3m3);
        return oa.localeCompare(ob);
      },
      render: (val, record) => {
        const alert = val || getOperationalAlert(record.Rainfall_mm, record.Max_Wind_Speed_kmh, record.Soil_Moisture_m3m3);
        const colorMap = {
          'Extreme Wind': '#B71C1C',
          'Heavy Rain/Mud': '#E65100',
          'High Wind': '#F57C00',
          'Irrigation Advised': '#0288D1',
          'Normal Ops': '#66BB6A',
        };
        const bg = colorMap[alert] || '#718096';
        return (
          <span
            style={{
              backgroundColor: bg,
              color: '#ffffff',
              padding: '4px 10px',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.2px',
              display: 'inline-block',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            {alert}
          </span>
        );
      },
    },
  ];

  return (
    <div className="weather-report-container">
      {/* 1. Header & Super Simple Weather Card */}
      <div className="weather-simple-card">
        <div className="simple-card-title">
          <span>🌤️</span> Weather Intelligence & Data Extractor
        </div>

        {/* 1. LOCATION SOURCE SELECTION */}
        <div className="source-selector-group">
          <span className="source-label">LOCATION SOURCE:</span>
          <Radio.Group
            value={locationMode}
            onChange={(e) => {
              setLocationMode(e.target.value);
              setGmapsError(false);
              setLatlonError(false);
            }}
            className="radio-options"
          >
            <Radio value="apiary" className="radio-option">
              Select Apiary
            </Radio>
            <Radio value="gmaps" className="radio-option">
              Google Maps Link
            </Radio>
            <Radio value="latlon" className="radio-option">
              Direct Lat / Long
            </Radio>
          </Radio.Group>
        </div>

        {/* 2. DYNAMIC INPUTS ROW */}
        <div className="inputs-row">
          {/* Option A: Apiary Dropdown */}
          {locationMode === 'apiary' && (
            <div className="input-field-group">
              <label>* Select Apiary / Location:</label>
              <Select
                showSearch
                placeholder="-- Select Existing Apiary --"
                loading={loadingApiaries}
                value={selectedApiaryId}
                onChange={(val) => setSelectedApiaryId(val)}
                filterOption={(input, option) => {
                  const children = option.props.children;
                  let label = '';
                  if (typeof children === 'string') label = children;
                  else if (Array.isArray(children)) label = children.map(c => typeof c === 'string' ? c : '').join(' ');
                  else label = String(option.props.value || '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                className="input-control select-control"
              >
                <Select.OptGroup label="⭐ Quick Presets">
                  {PRESET_LOCATIONS.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.name}
                    </Option>
                  ))}
                </Select.OptGroup>
                {apiariesList.length > 0 && (
                  <Select.OptGroup label={`🐝 Registered Apiaries (${apiariesList.length})`}>
                    {apiariesList.map((a) => (
                      <Option key={a.id} value={a.id}>
                        {a.name} {a.hasCoords ? `[${a.lat?.toFixed(3)}, ${a.lon?.toFixed(3)}]` : '(No GPS)'}
                      </Option>
                    ))}
                  </Select.OptGroup>
                )}
              </Select>
              <div style={{ minHeight: '22px', marginTop: '6px' }}>
                {(() => {
                  const selected = combinedLocations.find((l) => String(l.id) === String(selectedApiaryId) || String(l.name) === String(selectedApiaryId));
                  if (!selected) return null;
                  return (
                    <div style={{ fontSize: '12px', color: selected.hasCoords !== false && selected.lat ? '#2b6cb0' : '#d69e2e', fontWeight: 600 }}>
                      {selected.lat && selected.lon ? (
                        <span>📍 Coordinates: <strong>Lat {Number(selected.lat).toFixed(4)}, Lon {Number(selected.lon).toFixed(4)}</strong></span>
                      ) : (
                        <span>⚠️ No GPS coordinates attached to this apiary in database</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Option B: Google Maps Link Input */}
          {locationMode === 'gmaps' && (
            <div className="input-field-group">
              <label htmlFor="gmaps-url">* Paste Google Maps Link (with location pin):</label>
              <Input
                id="gmaps-url"
                className="input-control"
                placeholder="https://www.google.com/maps/@12.974148,77.5046096,15z..."
                value={gmapsUrl}
                onChange={(e) => handleGmapsInput(e.target.value)}
              />
              <div style={{ minHeight: '22px', marginTop: '6px' }}>
                {gmapsError && (
                  <span className="error-msg">
                    Invalid Google Maps link. Must contain valid coordinates (@lat,lon or q=lat,lon).
                  </span>
                )}
                {parsedGmapsCoords && (
                  <span style={{ color: '#2b6cb0', fontSize: '12px', fontWeight: 600 }}>
                    ✓ Detected: Lat {parsedGmapsCoords.lat}, Lon {parsedGmapsCoords.lon}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Option C: Manual Lat/Long Inputs */}
          {locationMode === 'latlon' && (
            <div className="input-field-group">
              <label>* Direct Coordinates (Min 4 decimals):</label>
              <div className="coordinates-dual-input">
                <Input
                  type="number"
                  step="0.0001"
                  className="input-control"
                  placeholder="Lat (e.g. 26.3322)"
                  value={inputLat}
                  onChange={(e) => {
                    setInputLat(e.target.value);
                    setLatlonError(false);
                  }}
                />
                <Input
                  type="number"
                  step="0.0001"
                  className="input-control"
                  placeholder="Lon (e.g. 79.6222)"
                  value={inputLon}
                  onChange={(e) => {
                    setInputLon(e.target.value);
                    setLatlonError(false);
                  }}
                />
              </div>
              <div style={{ minHeight: '22px', marginTop: '6px' }}>
                {latlonError && (
                  <span className="error-msg">Requires at least 4 decimal places for both coordinates.</span>
                )}
              </div>
            </div>
          )}

          {/* DATE RANGE INPUT */}
          <div className="input-field-group">
            <label>* Date Range (Default 90 Days / Any Custom Range):</label>
            <div className="date-picker-range-wrapper">
              <RangePicker
                style={{ width: '100%', height: '40px' }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="DD/MM/YYYY"
              />
            </div>
            <div style={{ minHeight: '22px', marginTop: '6px' }}></div>
          </div>

          {/* FETCH BUTTON */}
          <div className="input-field-group">
            <label style={{ visibility: 'hidden', height: '19px' }}>Action</label>
            <Button
              type="primary"
              className="btn-fetch-blue"
              loading={loading}
              onClick={handleFetchWeather}
              style={{ height: '40px', borderRadius: '6px', fontWeight: 700 }}
            >
              <span>⚡</span> Fetch Weather
            </Button>
            <div style={{ minHeight: '22px', marginTop: '6px' }}></div>
          </div>
        </div>
      </div>

      {/* 1.5 Collapsible Weather Rules & Biological Context Guide */}
      <Collapse
        bordered={false}
        className="weather-guide-accordion"
        style={{
          marginTop: 20,
          backgroundColor: '#ffffff',
          borderRadius: 10,
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#2d3748', fontSize: 14 }}>
              <span>📖</span> Weather Metrics, Biological Rules & Field Logistics Guide (Click to expand / collapse)
            </div>
          }
          key="1"
        >
          <Row gutter={[16, 16]}>
            {/* 1. SOIL STATUS */}
            <Col xs={24} md={8}>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', height: '100%' }}>
                <h5 style={{ fontWeight: 700, color: '#1E88E5', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🌱 Soil Status Thresholds
                </h5>
                <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12, lineHeight: '1.7', color: '#4a5568' }}>
                  <li>
                    <strong style={{ color: '#1E88E5' }}>SATURATED (&gt; 0.38 m³/m³)</strong>: Soil approaches total pore saturation. Heavy trucks, hive transport &amp; field staff get stuck in mud.
                  </li>
                  <li>
                    <strong style={{ color: '#4CAF50' }}>IDEAL (0.25 - 0.38 m³/m³)</strong>: Field capacity in standard loam/clay-loam soils. Optimal vehicle &amp; staff mobility.
                  </li>
                  <li>
                    <strong style={{ color: '#FFB300' }}>DRY (&lt; 0.25 m³/m³)</strong>: Low soil moisture; dry ground conditions.
                  </li>
                </ul>
              </div>
            </Col>

            {/* 2. FORAGING RISK */}
            <Col xs={24} md={8}>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', height: '100%' }}>
                <h5 style={{ fontWeight: 700, color: '#388E3C', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🐝 Foraging Risk (Biological Limits)
                </h5>
                <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12, lineHeight: '1.7', color: '#4a5568' }}>
                  <li>
                    <strong style={{ color: '#D32F2F' }}>NO-FLIGHT</strong>: Rain &gt; 10mm OR Wind &gt; 20 km/h OR Max Temp &gt; 38°C OR Min Temp &lt; 12°C. Flight efficiency drops; bees cluster inside hive.
                  </li>
                  <li>
                    <strong style={{ color: '#FFA000' }}>MARGINAL</strong>: Rain 2-10mm OR Wind 15-20 km/h. Limited flight with low efficiency.
                  </li>
                  <li>
                    <strong style={{ color: '#388E3C' }}>OPTIMAL</strong>: Ideal flight window; bees actively forage flora &amp; nectar.
                  </li>
                </ul>
              </div>
            </Col>

            {/* 3. OPERATIONAL ALERTS */}
            <Col xs={24} md={8}>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', height: '100%' }}>
                <h5 style={{ fontWeight: 700, color: '#E65100', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🚨 Operational Alerts
                </h5>
                <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12, lineHeight: '1.7', color: '#4a5568' }}>
                  <li>
                    <strong style={{ color: '#B71C1C' }}>Extreme Wind (&gt; 25 km/h)</strong>: Inspection hazardous (risk of blowing off lids &amp; bee stings).
                  </li>
                  <li>
                    <strong style={{ color: '#E65100' }}>Heavy Rain/Mud (&gt; 15mm + Soil &gt; 0.38)</strong>: Dirt roads become unnavigable mud; impacts night migrations.
                  </li>
                  <li>
                    <strong style={{ color: '#F57C00' }}>High Wind (20-25 km/h)</strong>: Caution during hive handling.
                  </li>
                  <li>
                    <strong style={{ color: '#0288D1' }}>Irrigation Advised</strong>: Soil &lt; 0.20 &amp; Rain &lt; 1mm.
                  </li>
                  <li>
                    <strong style={{ color: '#66BB6A' }}>Normal Ops</strong>: Standard operations permitted.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        </Panel>
      </Collapse>

      {/* 2. KPI Summary Widgets */}
      {statistics && (
        <div className="kpi-grid-wrapper">
          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#ebf8ff', color: '#3182ce' }}>
              📅
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Days / Records</span>
              <span className="kpi-value">{statistics.totalRecords}</span>
              <span className="kpi-subtext">Total entries</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#e6fffa', color: '#319795' }}>
              🌧️
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Total Rainfall</span>
              <span className="kpi-value">{statistics.totalRain} mm</span>
              <span className="kpi-subtext">Cumulative sum</span>
            </div>
          </div>

          <div className="kpi-card" style={{ borderLeft: '4px solid #388E3C' }}>
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#f0fff4', color: '#388e3c' }}>
              🐝
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Foraging Window</span>
              <span className="kpi-value" style={{ color: '#2e7d32' }}>{statistics.foragingOptimalPct}%</span>
              <span className="kpi-subtext">{statistics.optimalDaysCount} Optimal Days</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#fffaf0', color: '#dd6b20' }}>
              🌡️
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Avg Temp</span>
              <span className="kpi-value">{statistics.avgTemp} °C</span>
              <span className="kpi-subtext">Mean temperature</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#fff5f5', color: '#e53e3e' }}>
              🔥
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Max / Min Temp</span>
              <span className="kpi-value">
                {statistics.maxTemp}° / {statistics.minTemp}°
              </span>
              <span className="kpi-subtext">Peak extremes</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#f7fafc', color: '#4a5568' }}>
              💨
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Max Wind</span>
              <span className="kpi-value">{statistics.maxWind} km/h</span>
              <span className="kpi-subtext">Top gust</span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#f0fff4', color: '#38a169' }}>
              🌱
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Avg Soil</span>
              <span className="kpi-value">{statistics.avgSoil}</span>
              <span className="kpi-subtext">0-7cm m³/m³</span>
            </div>
          </div>
        </div>
      )}

      {/* 3. Results Table Card */}
      <div className="weather-table-card" style={{ marginTop: statistics ? 0 : 24 }}>
        <div className="weather-table-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h4 style={{ margin: 0, fontWeight: 700, color: '#2d3748' }}>
              📊 Daily Weather Records ({tableData.length})
            </h4>
            {tableData.length > 0 && (
              <Tag color="green">Ready for Export</Tag>
            )}
          </div>

          <div className="weather-actions">
            <Button
              type="primary"
              icon="download"
              disabled={tableData.length === 0}
              onClick={handleExportTableCSV}
              style={{ backgroundColor: '#38a169', borderColor: '#38a169', fontWeight: 600 }}
            >
              Export CSV
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={{
            pageSize: pageSize,
            onShowSizeChange: (current, size) => setPageSize(size),
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['100', '500', '1000', '2000', '5000', '10000'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: 1400 }}
          bordered
          size="middle"
        />
      </div>
    </div>
  );
};

export default WeatherReport;
